import React, { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

/**
 * Lightweight hover tooltip. Wraps any children (a span, a chip, an icon,
 * whatever) and shows a white portalled pill near the pointer while the
 * cursor is over the anchor. Cheaper cousin of MetaTooltip: no clamp
 * detection, no clip-aware flipping — just "always show while hovered."
 *
 *   <HoverTip text="Full text here">
 *     <span className="…">Short label</span>
 *   </HoverTip>
 *
 * The pop is portalled to `document.body` with `position: fixed` so it
 * escapes every ancestor overflow-clip. Set `disabled` (e.g. when the
 * text is empty) to render children with no hover behaviour attached.
 */
const HoverTip = ({ text, children, disabled }) => {
  const [tip, setTip] = useState(null);

  const show = useCallback(
    (e) => {
      if (disabled) return;
      if (!text) return;
      setTip({ x: e.clientX, y: e.clientY });
    },
    [disabled, text],
  );
  const move = useCallback(
    (e) => {
      setTip((prev) => (prev ? { x: e.clientX, y: e.clientY } : prev));
    },
    [],
  );
  const hide = useCallback(() => setTip(null), []);

  // Clone the child element to attach mouse listeners without wrapping
  // in an extra span (chip layout, grid children, etc. would break with
  // a wrapper). Only supports a single React element as children.
  const child = React.Children.only(children);
  const wrapped = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); if (child.props.onMouseEnter) child.props.onMouseEnter(e); },
    onMouseMove:  (e) => { move(e); if (child.props.onMouseMove)  child.props.onMouseMove(e);  },
    onMouseLeave: (e) => { hide();  if (child.props.onMouseLeave) child.props.onMouseLeave(e); },
  });

  return (
    <>
      {wrapped}
      {tip && !disabled && text && ReactDOM.createPortal(
        <div
          className="hover-tip"
          role="tooltip"
          // Position anchored to the cursor with a small offset so the
          // pop isn't under the pointer. Left/right clamp is a coarse
          // "don't spill off the viewport" — enough for chip labels.
          style={{
            position: "fixed",
            left: Math.max(6, Math.min(window.innerWidth - 6, tip.x + 12)),
            top: Math.max(6, tip.y - 12),
            transform: "translate(0, -100%)",
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
};

HoverTip.propTypes = {
  text: PropTypes.string,
  children: PropTypes.element.isRequired,
  disabled: PropTypes.bool,
};

export default HoverTip;
